<script setup lang="ts">
import { computed, defineEmits, defineProps, ref, watch } from 'vue'
import Modal from '../../dialogs/Modal.vue'
import BaseButtonSecondary from '../../base/BaseButtonSecondary.vue'
import { SelectOption } from '../../../typings'
import { useWalletState, WalletState } from '../../../utilities/account/useWallet'
import BaseInputSelect from '../../base/BaseInputSelect.vue'
import { BankAccount, postWalletMoneyWithdrawal } from '../../../utilities/api'
import { openAlertDialog, openConfirmDialog } from '../../../utilities/useDialog'
import { i18n } from '../../../localization'
import { AxiosError } from 'axios'
import { useForm } from 'vee-validate'
import Alert from '../../base/Alert.vue'
import { CashIcon, ExclamationCircleIcon } from '@heroicons/vue/solid'
import BaseButtonPrimary from '../../base/BaseButtonPrimary.vue'
import { formatCurrency } from '../../../utilities/helpers'
import BaseInputMoney from '../../base/BaseInputMoney.vue'
import { getAccountLink } from '../../../utilities/useLinks'

const props = defineProps<{
    modelValue: boolean
}>()

const emits = defineEmits(['update:modelValue'])

const walletStore = useWalletState() as WalletState

const open = ref(props.modelValue)

const maxAmount = 10000
const form = useForm()
watch(
    () => props.modelValue,
    (val) => (open.value = val)
)
watch(open, (val) => emits('update:modelValue', val))

const bankAccountSelectOptions: Array<SelectOption<string>> = walletStore.verifiedBankAccounts.map(
    (bankAccount: BankAccount) => {
        return {
            key: bankAccount.id,
            label:
                `${bankAccount.holder} - ${bankAccount.iban}` +
                (bankAccount.is_main ? ` (${i18n.global.t('bank_account.main_bank_account')})` : ''),
        }
    }
)

const amount = ref(null)
const bankAccount = ref(walletStore.verifiedBankAccounts?.find((bA) => bA.is_main)?.id)
const loading = ref(false)

const selectedBankAccount = computed(() => walletStore.verifiedBankAccounts.find((bA) => bA.id === bankAccount.value))

async function submit() {
    const { valid } = form.meta.value

    if (!valid) {
        openAlertDialog(i18n.global.t('dialog.formInvalid.title'), i18n.global.t('dialog.formInvalid.text'))
        return
    }

    loading.value = true
    const bankAccountId = bankAccountSelectOptions.length > 1 ? bankAccount.value : bankAccountSelectOptions[0].key
    try {
        await postWalletMoneyWithdrawal({ amount: amount.value, bank_account_id: bankAccountId })
        open.value = false
        openConfirmDialog(
            i18n.global.t('wallet.confirm_withdrawal_title'),
            i18n.global.t('wallet.confirm_withdrawal_description'),
            {
                type: 'Success',
                hideCancel: true,
                confirmText: i18n.global.t('dialog.button.ok'),
            }
        )
    } catch (error: AxiosError) {
        openAlertDialog(
            i18n.global.t('errors.general_error'),
            error.response?.data?.message || error.response?.data?.errorMessage
        )
    } finally {
        loading.value = false
        walletStore.fetchWalletDataSilent()
    }
}

function validateAmount(value): string | boolean {
    if (!value) {
        return i18n.global.t('validation_error.required')
    }
    if (value < 1) {
        return i18n.global.t('validation_error.min_amount', { amount: formatCurrency(1, walletStore.wallet.currency) })
    }
    if (value > walletStore.wallet.retrievable_balance) {
        return i18n.global.t('validation_error.min_amount_on_wallet', {
            balance: formatCurrency(walletStore.wallet.retrievable_balance, walletStore.wallet.currency),
        })
    }
    if (value > maxAmount) {
        return i18n.global.t('validation_error.max_amount', {
            amount: formatCurrency(maxAmount, walletStore.wallet.currency),
        })
    }
    return true
}

function goToVerification() {
    window.location.href = getAccountLink() + '?menu=Verification&tab=kyc_bank'
}
</script>

<template>
    <Modal v-model="open" size="md" :close-on-outside-click="false">
        <div>
            <h3 class="text-2xl font-bold text-gray-900">{{ $t('wallet.withdrawal_header') }}</h3>
            <div v-if="walletStore.verifiedBankAccounts.length === 0">
                <Alert
                    inline
                    :title-text="$t('wallet.verify_bank_account_alert_header')"
                    :description-text="$t('wallet.verify_bank_account_alert_description')">
                    <template #icon>
                        <CashIcon class="h-12 w-12 text-gray-300"></CashIcon>
                        <ExclamationCircleIcon
                            class="absolute bottom-0 right-[-5px] h-6 w-6 rounded-full bg-white text-red-400"></ExclamationCircleIcon>
                    </template>
                </Alert>

                <BaseButtonPrimary class="mt-4 w-full" size="lg" @click="goToVerification">
                    {{ $t('wallet.verify_bank_account_alert_action_button') }}
                </BaseButtonPrimary>
            </div>
            <div v-else>
                <p class="mt-2 text-sm font-medium text-gray-500">
                    {{ $t('wallet.withdrawal_description') }}
                </p>
                <div class="space-y-8">
                    <div>
                        <BaseInputSelect
                            v-if="bankAccountSelectOptions.length > 1"
                            v-model="bankAccount"
                            class="mt-4"
                            name="withdrawal_bank_account_select"
                            :label="$t('wallet.withdrawal_bank_account')"
                            :placeholder="$t('wallet.withdrawal_bank_account_select')"
                            :options="bankAccountSelectOptions" />
                    </div>

                    <div>
                        <BaseInputMoney
                            v-model="amount"
                            name="withdrawal_amount"
                            :currency="walletStore.wallet.currency"
                            :label="$t('wallet.withdrawal_amount')"
                            :rules="validateAmount" />
                        <div class="mt-2 text-right text-sm text-gray-400">
                            max: {{ formatCurrency(maxAmount, walletStore.wallet.currency, 0) }}
                        </div>
                    </div>

                    <div v-if="selectedBankAccount">
                        <p class="font-medium text-gray-900">{{ $t('wallet.withdrawal_account_description') }}:</p>

                        <dl class="mt-4 grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                            <div class="sm:col-span-1">
                                <dt class="font-medium text-gray-500">
                                    {{ $t('wallet.bank_account_holder') }}
                                </dt>
                                <dd class="mt-1 text-gray-900">{{ selectedBankAccount.holder }}</dd>
                            </div>
                            <div class="sm:col-span-1">
                                <dt class="font-medium text-gray-500">IBAN</dt>
                                <dd class="mt-1 text-gray-900">{{ selectedBankAccount.iban }}</dd>
                            </div>
                        </dl>
                    </div>

                    <BaseButtonSecondary
                        class="mt-4 w-full"
                        size="xl"
                        :loading="loading"
                        :disabled="loading"
                        type="button"
                        @click="submit">
                        {{ $t('wallet.withdrawal_button_submit') }}
                    </BaseButtonSecondary>
                </div>
            </div>
        </div>
    </Modal>
</template>
